import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { siteImages } from '../data/mock';
import { Mail, Lock, ArrowRight, LogIn, AlertCircle, Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = 'Sign In | Sport Fitt Pro';
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    login({ email: formData.email, firstName: 'Athlete', lastName: '' });
    navigate('/');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 pt-[104px] grid lg:grid-cols-2">
        {/* Left: Visual Side */}
        <div className="relative hidden lg:block bg-ink-950 overflow-hidden">
          <img
            src={siteImages.heroSecondary}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-ink-950 via-ink-950/70 to-athletic-950/60" />
          <div className="absolute inset-0 p-14 flex flex-col justify-between text-white">
            <div>
              <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Member Access</p>
              <h2 className="font-display text-6xl leading-none tracking-wider">
                WELCOME<br />
                <span className="text-athletic-500">BACK.</span>
              </h2>
              <p className="text-white/70 mt-5 max-w-sm leading-relaxed">
                Sign in to track orders, save favorites and get member-only product drops before anyone else.
              </p>
            </div>
            <div className="space-y-4">
              {['Faster checkout on every order','Exclusive early-access drops','Track orders & returns in one place'].map((t, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-8 h-8 flex-shrink-0 bg-athletic-500 flex items-center justify-center font-black text-xs">0{i+1}</span>
                  <span className="text-white/80 text-sm">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Form Side */}
        <div className="flex items-center justify-center p-6 sm:p-10 lg:p-14">
          <div className="w-full max-w-md">
            <div className="mb-10">
              <p className="text-athletic-500 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Sign In</p>
              <h1 className="font-display text-5xl text-ink-900 tracking-wider leading-none mb-3">GEAR UP.</h1>
              <p className="text-ink-500">Access your Sport Fitt Pro account to continue.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-start gap-2 px-4 py-3 bg-athletic-50 border-2 border-athletic-100 text-athletic-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="text-sm font-medium">{error}</span>
                </div>
              )}

              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-ink-900 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="w-full pl-11 pr-4 py-3.5 bg-ink-50 border-2 border-ink-100 focus:outline-none focus:border-athletic-500 text-ink-900 transition-colors"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-[11px] font-black uppercase tracking-wider text-ink-900">Password</label>
                  <Link to="/forgot-password" className="text-[11px] font-bold text-athletic-500 hover:text-athletic-600 uppercase tracking-wider">Forgot?</Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className="w-full pl-11 pr-11 py-3.5 bg-ink-50 border-2 border-ink-100 focus:outline-none focus:border-athletic-500 text-ink-900 transition-colors"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-ink-400 hover:text-athletic-500">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-black uppercase tracking-wider transition-all"
              >
                <LogIn className="w-5 h-5" /> Sign In <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            <div className="mt-8 pt-6 border-t-2 border-ink-100 text-center">
              <p className="text-ink-500 text-sm">
                New to Sport Fitt Pro?{' '}
                <Link to="/register" className="text-athletic-500 hover:text-athletic-600 font-black uppercase tracking-wider text-xs">
                  Create Account
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LoginPage;
